import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function TicketsList() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingTicket, setEditingTicket] = useState(null);
    const [formData, setFormData] = useState({ 
        name: '', 
        maleCount: 0, 
        femaleCount: 0, 
        active: true
    });

    useEffect(() => {
        fetchTickets();
    }, []);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const res = await axios.get('/speeddate/api/tickets');
            setTickets(res.data.filter(t => !t.EventId));
        } catch (err) {
            console.error("Error fetching tickets", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = { 
                ...formData, 
                EventId: null,
                maleCount: parseInt(formData.maleCount),
                femaleCount: parseInt(formData.femaleCount),
                price: 0 // Price is defined at event level
            };

            if (editingTicket) {
                await axios.put(`/speeddate/api/tickets/${editingTicket.id}`, data);
                window.Swal.fire({ title: 'Aggiornato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
            } else {
                await axios.post('/speeddate/api/tickets', data);
                window.Swal.fire({ title: 'Creato!', icon: 'success', timer: 1500, showConfirmButton: false, toast: true, position: 'top-end' });
            }
            resetForm();
            fetchTickets();
        } catch (err) {
            window.Swal.fire('Errore', err.response?.data?.error || 'Errore nel salvataggio', 'error');
        }
    };

    const resetForm = () => {
        setFormData({ name: '', maleCount: 0, femaleCount: 0, active: true });
        setEditingTicket(null);
    };

    const handleEdit = (ticket) => {
        setEditingTicket(ticket);
        setFormData({ 
            name: ticket.name, 
            maleCount: ticket.maleCount, 
            femaleCount: ticket.femaleCount, 
            active: ticket.active
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        const result = await window.Swal.fire({
            title: 'Sei sicuro?',
            text: "Questa operazione rimuoverà il tipo di biglietto.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla'
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`/speeddate/api/tickets/${id}`);
                fetchTickets();
                window.Swal.fire('Eliminato!', 'Il tipo di biglietto è stato rimosso.', 'success');
            } catch (err) {
                window.Swal.fire('Errore', 'Impossibile eliminare il tipo di biglietto.', 'error');
            }
        }
    };

    return (
        <div className="row g-4">
            <div className="col-lg-4">
                <div className="card border-0 shadow-sm glass">
                    <div className="card-header bg-transparent border-0 pt-4 px-4">
                        <h5 className="fw-bold mb-0">
                            <i className={`fas ${editingTicket ? 'fa-edit text-success' : 'fa-plus-circle text-primary'} me-2`}></i>
                            {editingTicket ? 'Modifica Tipo Biglietto' : 'Nuovo Tipo Biglietto'}
                        </h5>
                    </div>
                    <div className="card-body p-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="form-label small text-uppercase fw-bold text-muted">Nome Tipo Biglietto</label>
                                <input
                                    type="text"
                                    className="form-control rounded-3"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Es: Coppia, Uomo, 3 Amici..."
                                    required
                                />
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label small text-uppercase fw-bold text-muted">Uomini</label>
                                    <input
                                        type="number"
                                        className="form-control rounded-3"
                                        value={formData.maleCount}
                                        onChange={(e) => setFormData({ ...formData, maleCount: e.target.value })}
                                        min="0"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label small text-uppercase fw-bold text-muted">Donne</label>
                                    <input
                                        type="number"
                                        className="form-control rounded-3"
                                        value={formData.femaleCount}
                                        onChange={(e) => setFormData({ ...formData, femaleCount: e.target.value })}
                                        min="0"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="mb-4 form-check">
                                <input 
                                    type="checkbox" 
                                    className="form-check-input" 
                                    id="ticketActive" 
                                    checked={formData.active}
                                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                />
                                <label className="form-check-label small fw-bold" htmlFor="ticketActive">Tipo Attivo</label>
                            </div>
                            <div className="d-grid gap-2">
                                <button type="submit" className={`btn ${editingTicket ? 'btn-success' : 'btn-primary'} rounded-pill`}>
                                    <i className="fas fa-save me-2"></i> {editingTicket ? 'Aggiorna' : 'Salva'}
                                </button>
                                {editingTicket && (
                                    <button type="button" className="btn btn-link btn-sm text-secondary" onClick={resetForm}>
                                        Annulla
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="col-lg-8">
                <div className="card border-0 shadow-sm glass">
                    <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold"><i className="fas fa-ticket-alt text-primary me-2"></i>Anagrafica Tipi di Biglietto</h4>
                        <button className="btn btn-sm btn-outline-primary rounded-circle" onClick={fetchTickets} disabled={loading}>
                            <i className={`fas fa-sync ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>
                    <div className="card-body px-4">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="small text-uppercase fw-bold text-muted border-0">Tipo</th>
                                        <th className="small text-uppercase fw-bold text-muted border-0 text-center">Persone</th>
                                        <th className="small text-uppercase fw-bold text-muted border-0 text-end">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tickets.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-center py-5">
                                                {loading ? (
                                                    <div className="spinner-border text-primary" role="status"></div>
                                                ) : (
                                                    <div className="text-muted">
                                                        <i className="fas fa-ticket-alt fa-3x mb-3 opacity-25"></i>
                                                        <p className="mb-0">Nessun tipo di biglietto trovato</p>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : tickets.map((t) => (
                                        <tr key={t.id} className={!t.active ? 'opacity-50' : ''}>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className={`bg-light ${t.active ? 'text-primary' : 'text-muted'} rounded-circle p-2 me-3 d-flex align-items-center justify-content-center shadow-sm`} style={{ width: '40px', height: '40px' }}>
                                                        <i className="fas fa-ticket-alt"></i>
                                                    </div>
                                                    <div>
                                                        <span className="fw-bold d-block text-dark">{t.name}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex justify-content-center gap-2">
                                                    {t.maleCount > 0 && <span className="badge bg-primary-subtle text-primary rounded-pill"><i className="fas fa-mars me-1"></i>{t.maleCount}</span>}
                                                    {t.femaleCount > 0 && <span className="badge bg-danger-subtle text-danger rounded-pill"><i className="fas fa-venus me-1"></i>{t.femaleCount}</span>}
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button className="btn btn-link btn-sm text-primary px-2" onClick={() => handleEdit(t)}>
                                                    <i className="fas fa-edit"></i>
                                                </button>
                                                <button className="btn btn-link btn-sm text-danger px-2" onClick={() => handleDelete(t.id)}>
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
